import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { MaintenanceModule } from './modules/admin/pages/maintenance/maintenance.module';
import { CreditSuccessModule } from './modules/admin/pages/credit/credit-success/credit-success.module';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed in user to the '/example'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.module'
                    ).then((m) => m.AuthConfirmationRequiredModule),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'changepass',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.module').then(
                        (m) => m.AuthSignUpModule
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule
                    ),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () =>
                    import(
                        'app/modules/admin/dashboard/project/project.module'
                    ).then((m) => m.ProjectModule),
            },
            {
                path: 'users',
                loadChildren: () =>
                    import('app/modules/admin/users/users.module').then(
                        (m) => m.UsersModule
                    ),
            },
            {
                path: 'places',
                loadChildren: () =>
                    import('app/modules/admin/places/places.module').then(
                        (m) => m.PlacesModule
                    ),
            },
            //{path: 'sortiment', loadChildren: () => import('app/modules/admin/sortiment/sortiment.module').then(m => m.SortimentModule)},
            {
                path: 'example',
                loadChildren: () =>
                    import('app/modules/admin/example/example.module').then(
                        (m) => m.ExampleModule
                    ),
            },
            {
                path: 'partners',
                loadChildren: () =>
                    import('app/modules/admin/partners/partners.module').then(
                        (m) => m.PartnersModule
                    ),
            },
            {
                path: 'services',
                loadChildren: () =>
                    import('app/modules/admin/services/services.module').then(
                        (m) => m.ServicesModule
                    ),
            },
            {
                path: 'cities',
                loadChildren: () =>
                    import('app/modules/admin/cities/cities.module').then(
                        (m) => m.CitiesModule
                    ),
            },
            {
                path: 'sponsors',
                loadChildren: () =>
                    import('app/modules/admin/sponsors/sponsors.module').then(
                        (m) => m.SponsorsModule
                    ),
            },
            {
                path: 'reservations-overview',
                loadChildren: () =>
                    import(
                        'app/modules/admin/reservations-overview/reservations-overview.module'
                    ).then((m) => m.ReservationsOverviewModule),
            },
            //{path: 'orders-overview', loadChildren: () => import('app/modules/admin/orders-overview/orders-overview.module').then(m => m.OrdersOverviewModule)},
            {
                path: 'profile',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/profile/profile.module'
                    ).then((m) => m.ProfileModule),
            },
            //{path: 'credit', loadChildren: () => import('app/modules/admin/pages/credit/credit.module').then(m => m.CreditModule)},
            {
                path: 'support',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support/support.module'
                    ).then((m) => m.SupportModule),
            },
            {
                path: 'changelog',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/changelog/changelog.module'
                    ).then((m) => m.ChangelogModule),
            },
            {
                path: 'places-categories',
                loadChildren: () =>
                    import(
                        'app/modules/admin/places-category/places-category.module'
                    ).then((m) => m.PlacesCategoryModule),
            },
            //{path: 'sortiments-categories', loadChildren: () => import('app/modules/admin/sortiment-category/sortiments-category.module').then(m => m.SortimentsCategoryModule)},
            {
                path: 'places-criteria',
                loadChildren: () =>
                    import(
                        'app/modules/admin/places-criteria/places-criteria.module'
                    ).then((m) => m.PlacesCriteriaModule),
            },
            {
                path: 'rating-approve',
                loadChildren: () =>
                    import(
                        'app/modules/admin/rating-approve/rating-approve.module'
                    ).then((m) => m.RatingApproveModule),
            },
            {
                path: 'settings',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/settings/settings.module'
                    ).then((m) => m.SettingsModule),
            },
            {
                path: 'balance',
                loadChildren: () =>
                    import('app/modules/admin/balance/balance.module').then(
                        (m) => m.BalanceModule
                    ),
            },
            {
                path: 'notifications',
                loadChildren: () =>
                    import(
                        'app/modules/admin/notifications/notifications.module'
                    ).then((m) => m.NotificationsModule),
            },
            {
                path: 'employments',
                loadChildren: () =>
                    import(
                        'app/modules/admin/employments/employments.module'
                    ).then((m) => m.EmploymentsModule),
            },
            //{path: 'shipping-types', loadChildren: () => import('app/modules/admin/shipping-types/shipping-types.module').then(m => m.ShippingTypesModule)},
            {
                path: 'place-activities',
                loadChildren: () =>
                    import(
                        'app/modules/admin/places-activities/place-activities.module'
                    ).then((m) => m.PlaceActivitiesModule),
            },
            {
                path: 'global-settings',
                loadChildren: () =>
                    import(
                        'app/modules/admin/global-settings/global-settings.module'
                    ).then((m) => m.GlobalSettingsModule),
            },
            {
                path: 'credit-success',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/credit/credit-success/credit-success.module'
                    ).then((m) => m.CreditSuccessModule),
            },
            {
                path: 'municipality-levels',
                loadChildren: () =>
                    import(
                        'app/modules/admin/municipality-levels/municipality-levels.module'
                    ).then((m) => m.MunicipalityLevelsModule),
            },
            {
                path: 'extra-services',
                loadChildren: () =>
                    import(
                        'app/modules/admin/extra-services/extra-services.module'
                    ).then((m) => m.ExtraServicesModule),
            },
            {
                path: 'organizators',
                loadChildren: () =>
                    import(
                        'app/modules/admin/organizators/organizators.module'
                    ).then((m) => m.OrganizatorsModule),
            },

            //{path: 'complaints', loadChildren: () => import('app/modules/admin/complaints/complaints.module').then(m => m.ComplaintsModule)},
            // maintenance, error & Catch all
            {
                path: 'maintenance',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/maintenance/maintenance.module'
                    ).then((m) => m.MaintenanceModule),
            },
            {
                path: '500-internal-server-error',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-500/error-500.module'
                    ).then((m) => m.Error500Module),
            },
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-404/error-404.module'
                    ).then((m) => m.Error404Module),
            },
            { path: '**', redirectTo: '404-not-found' },
        ],
    },
];
